import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, CreditCard, ExternalLink, AlertTriangle, Star } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS_LABEL = {
  active: "Active", trialing: "Trial", canceling: "Canceling", past_due: "Past Due",
  paused: "Paused", canceled: "Canceled", pending: "Pending",
};
const STATUS_CLS = {
  active: "text-black bg-white border-white",
  trialing: "text-black bg-white border-white",
  canceling: "text-white border-white/60",
  past_due: "text-white border-white/60",
  paused: "text-white/60 border-white/30",
  canceled: "text-white/40 border-white/20",
  pending: "text-white/60 border-white/30",
};

export default function MemberBilling() {
  const [mems, setMems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/me");
      setMems(data.memberships || []);
    } catch {
      toast.error("Failed to load billing.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPortal = async (m) => {
    setOpening(m.id);
    try {
      const { data } = await api.post("/billing/portal", {
        membership_id: m.id, return_url: window.location.href,
      });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setOpening(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10 text-white/40" data-testid="billing-loading"><Loader2 className="animate-spin" /></div>;
  }
  if (!mems.length) {
    return (
      <div className="border border-white/10 bg-[#121212] p-6 text-white/50 text-sm" data-testid="billing-empty">
        No membership on file yet.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="member-billing">
      {mems.map((m) => (
        <div key={m.id} data-testid={`billing-card-${m.id}`} className="border border-white/10 bg-[#121212] p-6">
          {m.access?.restricted && (
            <div className="mb-4 flex items-start gap-3 border border-white/40 bg-white/5 p-3" data-testid={`billing-restricted-${m.id}`}>
              <AlertTriangle size={16} className="text-white mt-0.5 shrink-0" />
              <p className="text-sm text-white/80">
                Your access is limited due to a billing issue. Update your payment method to restore full Academy access.
              </p>
            </div>
          )}
          {!m.access?.restricted && m.status === "past_due" && m.access?.grace_until && (
            <div className="mb-4 flex items-start gap-3 border border-white/20 bg-white/[0.03] p-3" data-testid={`billing-grace-${m.id}`}>
              <AlertTriangle size={16} className="text-white/70 mt-0.5 shrink-0" />
              <p className="text-sm text-white/70">
                Payment failed — please update your card. Access stays active until {new Date(m.access.grace_until).toLocaleDateString()}.
              </p>
            </div>
          )}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-display font-bold uppercase tracking-tight text-2xl">{m.plan_name || "Membership"}</h3>
                {m.founding_athlete && (
                  <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase border border-white px-2 py-0.5 flex items-center gap-1"><Star size={11} /> Founding</span>
                )}
                <span className={`font-mono-accent text-[10px] tracking-[0.2em] uppercase border px-2 py-1 ${STATUS_CLS[m.status] || STATUS_CLS.pending}`} data-testid={`billing-status-${m.id}`}>
                  {STATUS_LABEL[m.status] || m.status}
                </span>
              </div>
              <p className="font-mono-accent text-3xl font-bold mt-2">${m.price}<span className="text-white/40 text-sm font-normal">/{m.interval || "mo"}</span></p>
              {m.current_period_end && (
                <p className="text-white/40 text-xs uppercase tracking-wide mt-2">
                  {m.status === "canceling" ? "Access ends" : "Next bill"}: {new Date(m.current_period_end).toLocaleDateString()}
                </p>
              )}
            </div>
            <button
              data-testid={`billing-manage-${m.id}`}
              onClick={() => openPortal(m)}
              disabled={!m.has_stripe || opening === m.id}
              className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white/85 transition-colors disabled:opacity-50"
            >
              {opening === m.id ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
              Manage Billing <ExternalLink size={12} />
            </button>
          </div>
          {!m.has_stripe && (
            <p className="text-white/40 text-xs mt-3">This membership is managed manually by your coach — no self-service billing.</p>
          )}
        </div>
      ))}
    </div>
  );
}
